import React, { useEffect, useState } from 'react';
import { Box } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import type { SupportedLanguage } from '@/libs/i18n';

const HUBSPOT_PORTAL_ID = '7552465';
const HUBSPOT_REGION = 'na1';
const HUBSPOT_SCRIPT_ID = 'hubspot-forms-7552465';
const HUBSPOT_SCRIPT_SRC = `https://js.hsforms.net/forms/embed/${HUBSPOT_PORTAL_ID}.js`;

const hubspotFormIds: Record<SupportedLanguage, string> = {
  en: '4d39d174-8298-4f13-a922-3d9cb937d0ad',
  fr: '614f2105-7b5d-4c0a-988c-662ad2e2e864',
  es: '4c797bbf-c519-4ed0-87b2-54221b46ffcb',
  de: '1a7887f1-f7de-4fa6-9f4e-7d67ce40c170'
};

let hubspotScriptPromise: Promise<void> | null = null;

const getHubspotLanguage = (language?: string): SupportedLanguage => {
  const code = language?.slice(0, 2).toLowerCase();
  return code === 'fr' || code === 'es' || code === 'de' ? code : 'en';
};

const ensureHubspotScript = (): Promise<void> => {
  const existingScript =
    (document.getElementById(HUBSPOT_SCRIPT_ID) as HTMLScriptElement | null) ||
    document.querySelector<HTMLScriptElement>(`script[src="${HUBSPOT_SCRIPT_SRC}"]`);

  if (existingScript) {
    existingScript.id = HUBSPOT_SCRIPT_ID;
    if (existingScript.dataset.loaded === 'true') return Promise.resolve();
    if (existingScript.dataset.managed === 'true' && hubspotScriptPromise) return hubspotScriptPromise;
    return Promise.resolve();
  }

  hubspotScriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.id = HUBSPOT_SCRIPT_ID;
    script.src = HUBSPOT_SCRIPT_SRC;
    script.defer = true;
    script.dataset.managed = 'true';
    script.addEventListener(
      'load',
      () => {
        script.dataset.loaded = 'true';
        resolve();
      },
      { once: true }
    );
    script.addEventListener(
      'error',
      () => {
        hubspotScriptPromise = null;
        reject(new Error('HubSpot forms script failed to load'));
      },
      { once: true }
    );
    document.head.appendChild(script);
  });

  return hubspotScriptPromise;
};

const ContactForm = () => {
  const { i18n } = useTranslation();
  const [isHubspotScriptReady, setIsHubspotScriptReady] = useState(false);
  const language = getHubspotLanguage(i18n.language);
  const formId = hubspotFormIds[language];

  useEffect(() => {
    let isMounted = true;

    ensureHubspotScript()
      .then(() => {
        if (isMounted) setIsHubspotScriptReady(true);
      })
      .catch(() => {
        if (isMounted) setIsHubspotScriptReady(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <Box py={{ base: '8', md: '12', lg: '24' }} className='eezi-hubspot-contact-form'>
      {isHubspotScriptReady && (
        <div
          key={formId}
          className='hs-form-frame'
          data-eezi-language={language}
          data-form-id={formId}
          data-portal-id={HUBSPOT_PORTAL_ID}
          data-region={HUBSPOT_REGION}
          data-testid='hubspot-contact-form'
        />
      )}
    </Box>
  );
};

export default ContactForm;
